import { GroupedInsights } from '@toggle/chart';
import {
  EventFeedEvent,
  isCatalystInsight,
  isEarningsInsight,
  isInsightFilter,
} from '@toggle/toggle';
import { isToday } from 'date-fns';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useInsightDrawer } from '~/hooks/use-insight-drawer/useInsightDrawer';
import { getEarningsInsightWithEventByEarningsId } from '~/react-query-hooks/events/use-event-feed/earnings-from-events';
import { mergeScenarioInsightsWithEvents } from '~/react-query-hooks/events/use-event-feed/scenario-from-events';
import {
  useEventFeed,
  useMarkEventRead,
} from '~/react-query-hooks/events/use-event-feed/useEventFeed';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { getExploreDirection } from '~/views/dashboard/whats-new-widget/components/insight-card/utils';
import { getConditionIcons } from '~/views/insights/components/insight-icons/utils';
import { InsightIconsRoot } from '~/views/insights/components/insight-icons-root/InsightIconsRoot';
import { InsightsTooltip } from '~/views/insights/components/insights-tooltip/InsightsTooltip';
import { ScenarioIconWrapper } from '~/views/dashboard/my-assets-widget/assets-table/insights-cell/insights-icon/InsightIcon.styles';
import { CatalystInsightIcon } from '~/widgets/catalyst-insights-drawer/catalyst-insight-icon/CatalystInsightIcon';
import { EarningsInsightIcon } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-icon/EarningsInsightIcon';

import * as S from './ChartInsightIcon.styles';
import { calculateIconPosition } from './utils';

interface ChartInsightIconProps {
  groupedInsights: GroupedInsights;
  insightIconRight: number;
  insightIconTop: number;
  yOffsetCount: number;
  isSimpleChart?: boolean;
  catalystEvents: EventFeedEvent[];
  earningsEvents: EventFeedEvent[];
}

export const ChartInsightIcon: FC<ChartInsightIconProps> = ({
  groupedInsights,
  insightIconTop,
  insightIconRight,
  yOffsetCount,
  isSimpleChart,
  catalystEvents,
  earningsEvents,
}) => {
  const { t } = useTranslation(['chart']);
  const { openDrawer: openInsightDrawer } = useInsightDrawer();

  const { mutate: markEventRead } = useMarkEventRead();

  const scenarioEntityTags = useMemo(
    () => [
      ...new Set(
        groupedInsights.insights
          .filter(isInsightFilter)
          .map(i => i.metadata.entity)
      ),
    ],
    [groupedInsights.insights]
  );
  const { data: scenarioEventFeedData } = useEventFeed(
    {
      entities: scenarioEntityTags,
      type: 'scenario-insight',
    },
    { enabled: scenarioEntityTags.length > 0 }
  );
  const scenarioInsightsWithEvent = useMemo(() => {
    const scenarioInsights = groupedInsights.insights.filter(isInsightFilter);
    const scenarioEvents =
      scenarioEventFeedData?.pages?.flatMap(page => page.events) ?? [];
    const merged = mergeScenarioInsightsWithEvents(
      scenarioInsights,
      scenarioEvents
    );
    return new Map(merged.map(m => [m.id, m]));
  }, [groupedInsights.insights, scenarioEventFeedData?.pages]);

  const singleIconWidth = isSimpleChart ? 12 : 24;
  const singleIconHeight = isSimpleChart ? 14 : 28;

  const verticalCombinedWidth = isSimpleChart ? 12 : 24;
  const verticalCombinedHeight = isSimpleChart ? 24 : 47;

  return (
    <>
      {groupedInsights.insights.map((insight, idx) => {
        if (isCatalystInsight(insight)) {
          const { iconY, iconRight } = calculateIconPosition({
            idx,
            iconHeight: singleIconHeight,
            iconWidth: singleIconWidth,
            insightIconTop,
            insightIconRight,
            yOffsetCount,
          });
          const matchedCatalystEvent = catalystEvents.find(
            e => e.source_id === insight.id
          );

          return (
            <S.IconContainer key={insight.id} $top={iconY} $right={iconRight}>
              <CatalystInsightIcon
                catalystInsightId={insight.id}
                subCategory={insight.sub_category}
                customSize={{
                  width: singleIconWidth,
                  height: singleIconHeight,
                }}
                isBordered
                identifier={insight.identifier}
                source={POSTHOG_TRACKING_SOURCES.CHART}
                eventId={matchedCatalystEvent?.id}
                read={matchedCatalystEvent?.read}
                category={insight.category}
              />
            </S.IconContainer>
          );
        }

        if (isEarningsInsight(insight)) {
          const { iconY, iconRight } = calculateIconPosition({
            idx,
            iconHeight: singleIconHeight,
            iconWidth: singleIconWidth,
            insightIconTop,
            insightIconRight,
            yOffsetCount,
          });
          const earningsInsightWithEvent =
            getEarningsInsightWithEventByEarningsId(
              earningsEvents,
              insight.earnings_id
            );

          return (
            <S.IconContainer key={insight.id} $top={iconY} $right={iconRight}>
              <EarningsInsightIcon
                entityTag={insight.entity_tag}
                earningsInsight={insight}
                eventId={earningsInsightWithEvent?.eventId}
                read={earningsInsightWithEvent?.read}
                customSize={{
                  width: singleIconWidth,
                  height: singleIconHeight,
                }}
                isBordered
                source={POSTHOG_TRACKING_SOURCES.CHART}
              />
            </S.IconContainer>
          );
        }

        if (!isInsightFilter(insight)) {
          return null;
        }

        const scenarioEvent = scenarioInsightsWithEvent.get(insight.id);
        const scenarioEventId = scenarioEvent?.eventId ?? '';
        const scenarioRead = scenarioEvent?.read ?? false;

        const isStarred = isToday(new Date(insight.created_at));
        const conditions = getConditionIcons({
          kind: insight.metadata.kind,
          conditions: insight.metadata.conditions,
        });
        const isCombined = conditions.length > 1;
        const iconWidth = isCombined ? verticalCombinedWidth : singleIconWidth;
        const iconHeight = isCombined
          ? verticalCombinedHeight
          : singleIconHeight;

        const { iconY, iconRight } = calculateIconPosition({
          idx,
          iconHeight: singleIconHeight,
          iconWidth: singleIconWidth,
          insightIconTop,
          insightIconRight,
          yOffsetCount,
        });

        const iconSize = {
          width: `${iconWidth}px`,
          height: `${iconHeight}px`,
        };

        const episodeLabel = t('chart:insightCard.episode', {
          count: insight.metadata.episode_count,
        });

        const handleScenarioClick = () => {
          if (scenarioEventId) {
            markEventRead(scenarioEventId);
          }
          openInsightDrawer(insight.id, { source: 'chart' });
        };

        return (
          <InsightsTooltip
            key={insight.id}
            conditions={conditions}
            direction={getExploreDirection(insight.metadata.direction)}
            customSize={{ width: '27px', height: '32px' }}
            isStarred={isToday(new Date(insight.created_at))}
            isMatched
            isRead={scenarioRead}
            horizon={insight.metadata.horizon}
            median={insight.metadata.median_return}
            stars={insight.metadata.stars}
            name={insight.card.name}
            entityTag={insight.metadata.entity}
            episodeLabel={episodeLabel}
          >
            <S.IconContainer
              key={insight.id}
              $top={iconY}
              $right={iconRight}
              onClick={handleScenarioClick}
            >
              <ScenarioIconWrapper $isRead={scenarioRead}>
                <InsightIconsRoot
                  conditions={conditions}
                  direction={getExploreDirection(insight.metadata.direction)}
                  customSize={iconSize}
                  isStarred={isStarred}
                  isCombined={isCombined}
                  layout="vertical"
                  isSparklesCondensed={isSimpleChart}
                />
              </ScenarioIconWrapper>
            </S.IconContainer>
          </InsightsTooltip>
        );
      })}
    </>
  );
};
